/* eslint-disable camelcase */
/**
 * Root interface for the x-auth module
 */
import { XV1App, AuthConfig, AuthToken, AuthContext, XV2App } from '@nab/x-types';

export { AuthConfig, AuthToken, AuthContext } from '@nab/x-types';

export interface ModifyCacheFn<C extends AuthConfig = AuthConfig> {
    (token: AuthToken<C>): Promise<void>;
}

export interface AuthStrategy<T extends AuthConfig = AuthConfig> {
    // Must acquire a token on-demand every time without any caching logic
    authenticate(config: T): Promise<AuthToken<T>>;

    // This function should return true if your auth strategy is capable of acquiring a token for the supplied config
    supports(config: T): boolean;
}

export interface TokenCacheStrategy<C extends AuthConfig = AuthConfig> {
    // Add a token to the cache
    addToken: ModifyCacheFn<C>;

    // Retrieve a token from the cache - should resolve with undefined if no token is available
    getToken(config: C): Promise<AuthToken<C>>;

    // Purge a token from the cache
    removeToken: ModifyCacheFn<C>;
}

/**
 * @todo new interface with clientId & scopes mandatory
 */
export interface ApigeeKerberosAuthConfig extends AuthConfig {
    appId: string;
    proxyName: string;
    authEndpoint?: string;
}

export interface DafScopeConfig {
    context: AuthContext;
    clientId?: string;
    scopes?: string | string[];
}

export interface DafAuthConfig extends AuthConfig, DafScopeConfig {
    appMetaData?: XV1App | XV2App;
    grantType?: DafGrantType;
    isSeed?: boolean;
}

export interface DafClientCredentialsAuthConfig extends DafAuthConfig {
    username?: string;
    password?: string; // Note this gets removed once login is complete
}

export interface DafSeedAuthConfig extends DafAuthConfig {
    isSeed?: true;
}

export interface DafAuthToken<C extends DafAuthConfig = DafAuthConfig> extends AuthToken<C> {
    idleTimeout?: number; // Number of milli's this token remains valid for without use
    issuedAt?: number; // Time of issue
    expires?: number; // Expiry time
}

export interface DafAnonymousAuthConfig extends DafAuthConfig {
    tokenType: 'Bearer';
    grantType: DafGrantType.nabAnonymous;
}

export type DafExchangeAuthToken = DafAuthToken<DafAuthConfig>;

export type DafAnonymousAuthToken = DafAuthToken<DafAnonymousAuthConfig>;

export interface MobileAuthConfig extends AuthConfig {
    appMetaData?: XV2App;
}

export interface MobileAuthToken<C extends MobileAuthConfig = MobileAuthConfig> extends AuthToken<C> {
    issuedAt?: number; // Time of issue
    expires?: number; // Expiry time
}

export enum DafGrantType {
    nabAnonymous = 'nab:anonymous',
    clientCredentials = 'client_credentials',
    tokenExchange = 'urn:ietf:params:oauth:grant-type:token-exchange',
    refreshToken = 'refresh_token',
    AuthorizationCode = 'authorization_code',
    Password = 'password',
    RefreshToken = 'refresh_token',
    ClientCredentials = 'client_credentials',
    UsernamePassword = 'nab:usernamePassword',
    DeviceRegIdPasscode = 'nab:deviceRegIdPasscode',
    DeviceRegIdPassword = 'nab:deviceRegIdPassword',
    DeviceRegIdFingerprint = 'nab:deviceRegIdFingerprint',
    DeviceRegIdFaceprint = 'nab:deviceRegIdFaceprint',
    IvrPasscode = 'nab:ivrPasscode',
    UsernameTokenCode = 'nab:usernameTokenCode',
    UsernamePasswordTokenCode = 'nab:usernamePasswordTokenCode',
    UsernameOTP = 'nab:usernameOTP',
    UsernamePasswordOTP = 'nab:usernamePasswordOTP',
    Saml2Bearer = 'urn:ietf:params:oauth:grant-type:saml2-bearer',
    JwtBearer = 'urn:ietf:params:oauth:grant-type:jwt-bearer',
    TokenExchange = 'urn:ietf:params:oauth:grant-type:token-exchange',
    Anonymous = 'nab:anonymous',
    IvrInAppCall = 'nab:ivrInAppCall',
    WssePasswordDigest = 'nab:wssePasswordDigest',
    Reauthentication = 'nab:reauthentication'
}

export enum DafAuthError {
    UNAUTHENTICATED = 'UNAUTHENTICATED',
    INTERNAL_SERVER_ERROR = 'INTERNAL_SERVER_ERROR',
    UNAUTHORISED = 'UNAUTHORISED',
    UNKNOWN_ERROR = 'UNKNOWN_ERROR',
    TPS_LIMIT_REACHED = 'TPS_LIMIT_REACHED'
}

export interface DafAuthResponse {
    access_token: string;
    token_type: string;
    expires_in?: number;
    idleTimeout?: number;
}
